import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

// Pictures stored on the site's own CDN (or in /public) are resized by Next.js.
// Anything else pasted into the CMS still shows, through a plain image tag.
const OPTIMIZED_HOST = /^https:\/\/[a-z0-9-]+\.cdn\.modulify\.website\//i

interface ProductImageProps {
    src: string | null
    alt: string
    sizes: string
    className?: string
    preload?: boolean
}

export const ProductImage: FC<ProductImageProps> = ({
    src,
    alt,
    sizes,
    className,
    preload = false
}): ReactNode => {
    if (!src) {
        return (
            <div
                aria-hidden='true'
                className={cn('absolute inset-0 flex items-center justify-center bg-linear-to-br from-sand to-stone', className)}
            >
                <span className='font-heading text-4xl italic text-gold-deep/60'>
                    Aveline
                </span>
            </div>
        )
    }

    if (src.startsWith('/') || OPTIMIZED_HOST.test(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                preload={preload}
                fetchPriority={preload ? 'high' : undefined}
                className={cn('object-cover', className)}
            />
        )
    }

    return (
        <img
            src={src}
            alt={alt}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 size-full object-cover', className)}
        />
    )
}
